import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";

const FamilyMeetingMinutesForm = ({ initialData = {}, onSubmit, onChange }) => {
    // ===== Local State =====
    const [clientName, setClientName] = useState("");
    const [reportWrittenBy, setReportWrittenBy] = useState("");
    const [date, setDate] = useState("");
    const [familyMembers, setFamilyMembers] = useState([{ name: "" }]);
    const [teamMembers, setTeamMembers] = useState([{ name: "" }]);
    const [agendaOfMeeting, setAgendaOfMeeting] = useState("");
    const [summaryOfDiscussion, setSummaryOfDiscussion] = useState("");
    const [actionPlanAgreedUpon, setActionPlanAgreedUpon] = useState("");

    // ✅ Sync initialData (for Edit form)
    useEffect(() => {
        if (initialData && Object.keys(initialData).length > 0) {
            setClientName(initialData.clientName || "");
            setReportWrittenBy(initialData.reportWrittenBy || "");
            setDate(initialData.date || "");
            setFamilyMembers(
                initialData.familyMembers?.length > 0
                    ? initialData.familyMembers
                    : [{ name: "" }]
            );
            setTeamMembers(
                initialData.teamMembers?.length > 0
                    ? initialData.teamMembers
                    : [{ name: "" }]
            );
            setAgendaOfMeeting(initialData.agendaOfMeeting || "");
            setSummaryOfDiscussion(initialData.summaryOfDiscussion || "");
            setActionPlanAgreedUpon(initialData.actionPlanAgreedUpon || "");
        }
    }, [initialData]);

    // ===== Dynamic Row Handlers =====
    const handleAddFamilyRow = () => setFamilyMembers([...familyMembers, { name: "" }]);
    const handleRemoveFamilyRow = (index) =>
        setFamilyMembers(familyMembers.filter((_, i) => i !== index));
    const handleFamilyChange = (index, value) => {
        const updated = [...familyMembers];
        updated[index].name = value;
        setFamilyMembers(updated);
    };

    const handleAddTeamRow = () => setTeamMembers([...teamMembers, { name: "" }]);
    const handleRemoveTeamRow = (index) =>
        setTeamMembers(teamMembers.filter((_, i) => i !== index));
    const handleTeamChange = (index, value) => {
        const updated = [...teamMembers];
        updated[index].name = value;
        setTeamMembers(updated);
    };


    useEffect(() => {
        if (onChange) {
            onChange({
                clientName,
                reportWrittenBy,
                date,
                familyMembers,
                teamMembers,
                agendaOfMeeting,
                summaryOfDiscussion,
                actionPlanAgreedUpon,
            });
        }
    }, [
        clientName,
        reportWrittenBy,
        date,
        familyMembers,
        teamMembers,
        agendaOfMeeting,
        summaryOfDiscussion,
        actionPlanAgreedUpon,
    ]);

    // ===== Submit Handler =====
    const handleSubmit = (e) => {
        e.preventDefault();

        const formData = {
            clientName,
            reportWrittenBy,
            date,
            familyMembers,
            teamMembers,
            agendaOfMeeting,
            summaryOfDiscussion,
            actionPlanAgreedUpon,
        };

        if (onSubmit) onSubmit(formData);
    };

    return (
        <form onSubmit={handleSubmit} className="p-4">
            <div className="col-12 mb-4">
                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
                    Family Meeting Minutes
                </small>
            </div>

            {/* Basic Info */}
            <div className="row mb-4">
                <div className="col-md-4">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            className="form-control"
                            value={clientName}
                            onChange={(e) => setClientName(e.target.value)}
                            placeholder="Client Name"
                            required
                            readOnly={!!initialData?.clientName} // ⭐ ADD THIS PROPERTY TO LOCK FIELD
                        />
                        <label>Client Name</label>
                    </div>
                </div>

                <div className="col-md-4">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            className="form-control"
                            value={reportWrittenBy}
                            onChange={(e) => setReportWrittenBy(e.target.value)}
                            placeholder="Report Written By"
                            required
                        />
                        <label>Report Written By</label>
                    </div>
                </div>

                <div className="col-md-4">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="date"
                            className="form-control"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            required
                        />
                        <label>Date</label>
                    </div>
                </div>
            </div>

            {/* Family & Team Members */}
            <div className="col-12 mb-4">
                <div className="row g-3">
                    {/* Family Members */}
                    <div className="col-md-6">
                        <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
                            Family Members Present
                        </small>
                        {familyMembers.map((member, index) => (
                            <div
                                className="row align-items-center mb-2 g-2 border rounded-3 p-4 bg-light-subtle"
                                key={`family-${index}`}
                            >
                                <div className="col-md-1 text-center">
                                    <span className="text-muted">{index + 1}.</span>
                                </div>
                                <div className="col-md-10">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={member.name}
                                            onChange={(e) =>
                                                handleFamilyChange(index, e.target.value)
                                            }
                                            placeholder="Name"
                                        />
                                        <label>Name</label>
                                    </div>
                                </div>
                                <div className="col-md-1">
                                    {familyMembers.length > 1 && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger btn-md w-100"
                                            onClick={() => handleRemoveFamilyRow(index)}
                                        >
                                            <i className="ri-close-line"></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm mt-2 w-100"
                            onClick={handleAddFamilyRow}
                        >
                            <i className="ri-add-line"></i> Add Family Member
                        </button>
                    </div>

                    {/* Team Members */}
                    <div className="col-md-6">
                        <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
                            Team Members Present
                        </small>
                        {teamMembers.map((member, index) => (
                            <div
                                className="row align-items-center mb-2 g-2 border rounded-3 p-4 bg-light-subtle"
                                key={`team-${index}`}
                            >
                                <div className="col-md-1 text-center">
                                    <span className="text-muted">{index + 1}.</span>
                                </div>
                                <div className="col-md-10">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={member.name}
                                            onChange={(e) =>
                                                handleTeamChange(index, e.target.value)
                                            }
                                            placeholder="Name"
                                        />
                                        <label>Name</label>
                                    </div>
                                </div>
                                <div className="col-md-1">
                                    {teamMembers.length > 1 && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger btn-md w-100"
                                            onClick={() => handleRemoveTeamRow(index)}
                                        >
                                            <i className="ri-close-line"></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm mt-2 w-100"
                            onClick={handleAddTeamRow}
                        >
                            <i className="ri-add-line"></i> Add Team Member
                        </button>
                    </div>
                </div>
            </div>

            {/* Text Areas */}
            <div className="col-md-12 mb-4">
                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
                    Agenda of Meeting
                </small>
                <textarea
                    className="form-control"
                    rows="3"
                    value={agendaOfMeeting}
                    onChange={(e) => setAgendaOfMeeting(e.target.value)}
                    placeholder="Enter the agenda of meeting..."
                ></textarea>
            </div>

            <div className="col-md-12 mb-4">
                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
                    Summary of Discussion
                </small>
                <textarea
                    className="form-control"
                    rows="3"
                    value={summaryOfDiscussion}
                    onChange={(e) => setSummaryOfDiscussion(e.target.value)}
                    placeholder="Enter summary of discussion..."
                ></textarea>
            </div>

            <div className="col-md-12 mb-4">
                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
                    Action Plan Agreed Upon
                </small>
                <textarea
                    className="form-control"
                    rows="3"
                    value={actionPlanAgreedUpon}
                    onChange={(e) => setActionPlanAgreedUpon(e.target.value)}
                    placeholder="Enter the agreed action plan..."
                ></textarea>
            </div>

            {/* <div className="col-12 d-flex justify-content-end mt-4">
                <div className="col-md-2 d-flex justify-content-end">
                    <button type="submit" className="btn btn-primary px-4">
                        <i className="ri-save-3-line me-1"></i> Save Record
                    </button>
                </div>
            </div> */}
        </form>
    );
};

export default FamilyMeetingMinutesForm;
